/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';
import {csvParse} from 'd3';
import {DSVRowArray} from 'd3-dsv';
import moment from 'moment';

import store from 'store';
import {IAppStore} from 'store/reducers';
import {getForkReports} from 'services/analytics.service';
import {IPortalError, transformToPortalError} from 'utility/error-handler';
import {IReport, IStartEnd} from 'interfaces/analyticsProps';
import {mergeSimilarCSVFiles} from 'components/shared/utils';

export const CHANNEL_FORKING_HISTORY_FETCHING = 'CHANNEL_FORKING_HISTORY_FETCHING' as const;
export const CHANNEL_FORKING_HISTORY_DONE_FETCHING = 'CHANNEL_FORKING_HISTORY_DONE_FETCHING' as const;
export const CHANNEL_FORKING_HISTORY_ERROR_FETCHING = 'CHANNEL_FORKING_HISTORY_ERROR_FETCHING' as const;
export const CLEAR_FORKING_HISTORY = 'CLEAR_FORKING_HISTORY' as const;
export const SET_FORKING_HISTORY_QUERY_DATA = 'SET_FORKING_HISTORY_QUERY_DATA' as const;
export const SET_FORKING_HISTORY_CHANNEL_IDS = 'SET_FORKING_HISTORY_CHANNEL_IDS' as const;
export const FORKING_HISTORY_SET_CSV = 'FORKING_HISTORY_SET_CSV' as const;

export const forkingHistorySelector = ({forkingHistory}: IAppStore): IAppStore['forkingHistory'] =>
  forkingHistory;
export const forkingHistoryFetchingSelector = ({forkingHistory}: IAppStore): boolean =>
  forkingHistory.isFetching;
export const forkingHistoryErrorSelector = ({forkingHistory}: IAppStore): IPortalError =>
  forkingHistory.error;
export const forkingHistoryQuerySelector = ({forkingHistory}: IAppStore): IStartEnd =>
  forkingHistory.queryData;
export const forkingHistoryCSVSelector = ({forkingHistory}: IAppStore): string => forkingHistory.csv;

interface ISetForkingHistoryQueryData {
  type: typeof SET_FORKING_HISTORY_QUERY_DATA;
  payload: IStartEnd;
}
export const setForkingHistoryQueryData = (data: IStartEnd) => async(
  dispatch: Dispatch<ISetForkingHistoryQueryData>
): Promise<void> => {
  dispatch({
    type: SET_FORKING_HISTORY_QUERY_DATA,
    payload: data
  });
};

interface ISetForkingHistoryCSVFile {
  type: typeof FORKING_HISTORY_SET_CSV;
  payload: string;
}
export const setForkingHistoryCSVFile = (payload: string) => (
  dispatch: Dispatch<ISetForkingHistoryCSVFile>
): void => {
  dispatch({
    type: FORKING_HISTORY_SET_CSV,
    payload
  });
};

interface ISetForkingHistoryChannelIds {
  type: typeof SET_FORKING_HISTORY_CHANNEL_IDS;
  payload: string[];
}
export const setForkingHistoryChannelIds = (payload: string[]) => async(
  dispatch: Dispatch<ISetForkingHistoryChannelIds>
): Promise<void> => {
  dispatch({
    type: SET_FORKING_HISTORY_CHANNEL_IDS,
    payload
  });
};

interface IRequestForkingHistoryAction {
  type: typeof CHANNEL_FORKING_HISTORY_FETCHING;
}
interface IReceiveForkingHistoryAction {
  type: typeof CHANNEL_FORKING_HISTORY_DONE_FETCHING;
  payload: DSVRowArray;
}
interface IFailedForkingHistoryAction {
  type: typeof CHANNEL_FORKING_HISTORY_ERROR_FETCHING;
  error: null | IPortalError;
}
interface IClearForkingHistoryAction {
  type: typeof CLEAR_FORKING_HISTORY;
}
export type FetchingForkingHistoryActions =
  | IRequestForkingHistoryAction
  | IReceiveForkingHistoryAction
  | IFailedForkingHistoryAction
  | IClearForkingHistoryAction;
interface IGetChannelForkingHistoryActions {
  request: () => IRequestForkingHistoryAction;
  receive: (payload: DSVRowArray) => IReceiveForkingHistoryAction;
  failed: (error: null | IPortalError) => IFailedForkingHistoryAction;
  clear: () => IClearForkingHistoryAction;
}
export const fetchChannelForkingHistoryActions: IGetChannelForkingHistoryActions = {
  request: () => ({type: CHANNEL_FORKING_HISTORY_FETCHING}),
  receive: payload => ({
    type: CHANNEL_FORKING_HISTORY_DONE_FETCHING,
    payload
  }),
  failed: error => ({
    type: CHANNEL_FORKING_HISTORY_ERROR_FETCHING,
    error
  }),
  clear: () => ({type: CLEAR_FORKING_HISTORY})
};

export const fetchChannelForkingHistory = (
  data: IReport,
  refresh = false,
  callBack?: () => null
) => async(
  dispatch: Dispatch<
    FetchingForkingHistoryActions | ISetForkingHistoryQueryData | ISetForkingHistoryCSVFile
  >
): Promise<void> => {
  const {receive, request, failed, clear} = fetchChannelForkingHistoryActions;
  const state = store.getState();
  const latterCSV: string = forkingHistoryCSVSelector(state);

  dispatch(request());

  try {
    const [sourceChannelsReport, destinationChannelsReport] = await getForkReports(data);

    if (refresh) {
      dispatch(clear());
    }

    const queryData = moment.utc(data.end).isBefore(moment.utc(), 'day') ? {
      start: data.start,
      end: null
    } : data;

    setForkingHistoryQueryData(queryData)(dispatch);

    const mergedResponse = mergeSimilarCSVFiles(
      sourceChannelsReport,
      destinationChannelsReport
    );

    setForkingHistoryCSVFile(
      latterCSV ? mergeSimilarCSVFiles(mergedResponse, latterCSV) : mergedResponse
    )(dispatch);

    const forkingHistory: DSVRowArray = csvParse(mergedResponse);

    dispatch(receive(forkingHistory));

    if (callBack) {
      callBack();
    }
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IClearForkingHistory {
  type: typeof CLEAR_FORKING_HISTORY;
}
export const clearForkingHistory = () => async(dispatch: Dispatch<IClearForkingHistory>): Promise<void> => {
  dispatch({type: CLEAR_FORKING_HISTORY});
};

export type ForkingHistoryActions =
  | FetchingForkingHistoryActions
  | ISetForkingHistoryQueryData
  | ISetForkingHistoryCSVFile
  | ISetForkingHistoryChannelIds
  | IClearForkingHistory;